import { AxiosResponse } from 'axios'
import { normalize, schema } from 'normalizr'
import { ManualTripGroupRidesDTO } from 'src/types/trip-group-rides'
import { TripStages, TripStatuses } from '../constants/trip-statuses'
import {
  IActiveTripResponse,
  IActiveTripResponseV2,
  ISimpleDriverDTO,
  IPagination,
  IProviderProductPricing,
  ITripHistoryObject,
  ITripRequestObject,
  ITripStatus,
  ITripFilters,
  IWaypointFilters,
  IUserTrips,
  IMapBound,
  IManualCompleteTripState,
  IDropOffNote,
  ITripEdit,
  ITripAvailableProduct,
  IAvailabilityDTO,
  IIntegrationProductResponse,
  IDailyTripLogReq,
  ICountTripStatusFilter,
  IServiceListFilterDTO,
  WeatherDTO,
  WeatherQueryParams,
  ITripLogReq,
  ITripSummaryFiltersDTO,
  ITripSummaryResponseDTO,
  IScheduledRequestedTripsFiltersDTO,
  IUpdateOptimizedTripsDTO,
  GenerateRecurringTripsRequestDTO,
  TempVehicleCountByServiceResponseDTO,
  IGetScheduledRequestedTripsResponseDTO,
  IPushNotificationPayload
} from '../types'
import Paths from '../utils/paths'
import { serverApi } from '../utils/request'
import { IPassengerDetailsPayload } from 'src/components/trips/trip-detail/update-passenger-details-modal/UpdatePassengerDetailsModal'
import { IDriverManifestsAlertReport, IDriverManifestsRetrieveParameters } from 'src/types/driver-manifests'

class TripService {
  private schemaTrips = new schema.Entity('trips')

  public async fetchRecentTrips(riderId: string, organizationId: string): Promise<any> {
    return serverApi
      .get(Paths.trips.recent, {
        params: { riderId, organizationId }
      })
      .then(({ data }) => data)
  }

  public fetchActiveTrips(
    organizationId: string,
    stage: string,
    numberOfRecords: number
  ): Promise<IActiveTripResponse[]> {
    return serverApi
      .get(Paths.trips.getLastActiveTripsByOrganizationAndStage, {
        params: {
          numberOfRecords,
          organizationId,
          stage
        }
      })
      .then(({ data }) => {
        const { entities } = normalize(data, [this.schemaTrips])
        return entities.trips
      })
  }

  public async fetchLastActiveTrips(filters: ITripFilters, pagination?: IPagination): Promise<any> {
    const params: any = { ...filters }

    if (pagination && !isNaN(pagination.skip) && !isNaN(pagination.take)) {
      params.skip = pagination.skip
      params.take = pagination.take
    }

    const result: any = await serverApi.get(Paths.trips.getLastActiveTrips, { params })
    const items = result.data && result.data.items ? result.data.items : []
    const total = result.data && result.data.total ? result.data.total : 0
    return { items, total }
  }

  public async fetchWaypoints(filters: IWaypointFilters): Promise<any> {
    const params: any = { ...filters }

    const result: any = await serverApi.get(Paths.trips.waypointIndex, { params })
    const items = result.data && result.data.items ? result.data.items : []
    const total = result.data && result.data.total ? result.data.total : 0
    return { items, total }
  }

  public async fetchScheduledTrips(filters: ITripFilters, pagination?: IPagination): Promise<any> {
    const params: any = { ...filters }
    if (pagination && !isNaN(pagination.skip) && !isNaN(pagination.take)) {
      params.skip = pagination.skip
      params.take = pagination.take
    }

    const result: any = await serverApi.get(Paths.trips.scheduled, { params })
    const items = result.data && result.data.items ? result.data.items : []
    const total = result.data && result.data.total ? result.data.total : 0
    return { items, total }
  }

  public fetchUserTrips(userId: string): Promise<IUserTrips[]> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve([])
      }, 1000)
    })
  }

  public fetchTripsHistory(orgId: string): Promise<ITripHistoryObject[]> {
    return serverApi
      .get<any>(Paths.trips.organizationTripHistory, { params: { organizationId: orgId } })
      .then(({ data }) => {
        if (!data || data.total === 0) {
          return []
        }
        return data.items
      })
  }

  public fetchOrganizationTrips(organizationId: string, pagination?: IPagination): Promise<ITripHistoryObject[]> {
    const params: any = { organizationId }
    if (pagination && !isNaN(pagination.skip) && !isNaN(pagination.take)) {
      params.skip = pagination.skip
      params.take = pagination.take
    }

    return serverApi.get<any>(Paths.trips.organizationTripHistory, { params }).then(({ data }) => {
      if (!data || data.total === 0) {
        return []
      }
      return data
    })
  }

  // public async calculateProviderProductPricing(request: any, product: any) {
  //   //TODO: this assumes a single rider trip for now
  //   //In the future riderTrips array might contain multiple pickups/dropoffs
  //   const riderTrip = request.riderTrips[0]
  //   const result = await serverApi.get<IProviderProductPricing>(Paths.providerProduct.calculateOnePricing, {
  //     params: {
  //       pickupLatitude: riderTrip.pickupLocation.latitude,
  //       pickupLongitude: riderTrip.pickupLocation.longitude,
  //       dropoffLatitude: riderTrip.dropoffLocation.latitude,
  //       dropoffLongitude: riderTrip.dropoffLocation.longitude,
  //       productId: product.id,
  //       riderCount: 1 //TODO change when request.riderCount is available
  //     }
  //   })
  //   return {
  //     [result.data.id]: {
  //       productId: result.data.id,
  //       cost: result.data.cost,
  //       fare: result.data.fare
  //     }
  //   }
  // }

  public async updateStatus(tripId: string, comment: string, tripStatus: string): Promise<string> {
    const path = Paths.trips.updateStatus.replace(':id', tripId)
    const result = await serverApi.patch<any>(path, {
      comment,
      tripId,
      createdAt: new Date(),
      status: tripStatus
    })
    return result.data.trip.id
  }

  public async getUberOperatorDriver(tripId: string, phoneNumber: string): Promise<any> {
    return serverApi.get(`${Paths.trips.base}/uberOperatorNumber/${tripId}/${phoneNumber}`)
  }

  public async create(tripRequest: ITripRequestObject): Promise<any> {
    return serverApi.post(Paths.trips.base, tripRequest)
  }

  public async addStop(stop: any, tripId: string): Promise<any> {
    const path = Paths.trips.addStop.replace(':id', tripId)
    return await serverApi.post(path, stop)
  }

  public async schedule(tripRequest: ITripRequestObject): Promise<any> {
    return serverApi.post(Paths.trips.scheduled, tripRequest)
  }

  public async assignedDispatchTrip(tripRequest: ITripRequestObject): Promise<any> {
    return serverApi.post(Paths.trips.assignedDispatchTrip, tripRequest)
  }

  public async fetchIntegrationProduct(
    riderId: string,
    dto: { product: IIntegrationProductResponse; tripRequest: IAvailabilityDTO }
  ): Promise<any> {
    const path = Paths.rider.integrationProduct.replace(':riderId', riderId)
    return serverApi.post(path, dto)
  }

  public async scheduleRecurringTrip(tripRequest: ITripRequestObject): Promise<any> {
    return serverApi.post(Paths.trips.recurTrip, tripRequest)
  }

  public async generateRecurringTrips(request: GenerateRecurringTripsRequestDTO): Promise<any> {
    return await serverApi.post(Paths.trips.generateRecurringTrips, request)
  }

  public async getRecuringTripByRiderId(riderId: string): Promise<any> {
    return await serverApi.get<any>(`${Paths.trips.base}/recurringTrips/${riderId}`)
  }

  public async destroyRecurringTask(recurringTripId: string): Promise<any> {
    return await serverApi.delete<any>(`${Paths.trips.recurTrip}/${recurringTripId}`)
  }
  public async get(tripId: string): Promise<IActiveTripResponseV2> {
    const result = await serverApi.get<any>(`${Paths.trips.base}/${tripId}`)
    return Object.assign({} as IActiveTripResponse, result.data)
  }

  public async fetch(params: ITripFilters): Promise<{ total: number; items: IActiveTripResponseV2[] }> {
    if (!params.orgId && (!params.productIds || !params.productIds.length)) {
      return { total: 0, items: [] }
    }
    const result =
      params.serviceIds && !!params.serviceIds.length
        ? await serverApi.post<any>(`${Paths.trips.web}`, { ...params })
        : await serverApi.get<any>(`${Paths.trips.web}`, { params })
    return result.data
  }

  public async fetchSummary(params: ITripSummaryFiltersDTO): Promise<ITripSummaryResponseDTO> {
    if (!params.orgId && (!params.products || !params.products.length)) {
      return { total: 0, items: [] }
    }
    const result = await serverApi.get<any>(`${Paths.trips.summary}`, { params })
    return result.data
  }

  public async fetchFeedback(params: ITripFilters): Promise<{ total: number; items: IActiveTripResponseV2[] }> {
    if (!params.orgId && (!params.productIds || !params.productIds.length)) {
      return { total: 0, items: [] }
    }
    const result = await serverApi.get<any>(`${Paths.tripsFeedbackBaseUrl}`, { params })
    return result.data
  }

  public async receipts(tripId: string): Promise<any> {
    const result = await serverApi.get<any>(`${Paths.payment.tripReceipts}/${tripId}`)
    return result.data
  }

  public async getManyStatusesByTripId(tripId: string): Promise<any> {
    return serverApi
      .get<any>(`${Paths.trips.getManyStatusesByTripId}/${tripId}`)
      .then((result: AxiosResponse<ITripStatus>) => result.data)
  }

  public async resendRequest(tripId: string): Promise<IActiveTripResponseV2> {
    const path = Paths.trips.updateStatus.replace(':id', tripId)
    const result = await serverApi.patch<IActiveTripResponseV2>(path, {
      tripId,
      createdAt: new Date(),
      status: TripStatuses.REQUESTED
    })
    return result.data
  }

  public async getTripDriverLocation(tripId: string): Promise<any> {
    const path = Paths.dispatch.tripDriverLocation.replace(':tripId', tripId)
    return serverApi.get<any>(path).then((result: AxiosResponse<any>) => result.data)
  }

  public async filterByZone(payload: ITripFilters): Promise<any> {
    return serverApi.post<any>(Paths.trips.zoneTrips, payload).then((result: AxiosResponse<any>) => result.data)
  }

  public async assignOperator(
    tripId: string,
    operator: ISimpleDriverDTO,
    setScheduled: boolean = true,
    dispatchAssignedDriver?: boolean
  ): Promise<any> {
    if (setScheduled) {
      let path = Paths.trips.assignOperator.replace(':id', tripId)
      const result = await serverApi.post<IActiveTripResponseV2>(path, {
        operatorId: operator.id,
        firstName: operator.firstName,
        lastName: operator.lastName,
        phone: operator.phone
      })

      if (result && result.data) {
        path = Paths.trips.updateStatus.replace(':id', tripId)
        await serverApi.patch<IActiveTripResponseV2>(path, {
          tripId,
          createdAt: new Date(),
          status: TripStatuses.SCHEDULED,
          data: {
            operator: {
              id: operator.operatorId,
              firstName: operator.firstName,
              lastName: operator.lastName,
              phone: operator.phone
            }
          }
        })
      }

      return result.data
    } else if (dispatchAssignedDriver) {
      let path = Paths.trips.assignOperator.replace(':id', tripId)
      const result = await serverApi.post<IActiveTripResponseV2>(path, {
        operatorId: operator.id,
        firstName: operator.firstName,
        lastName: operator.lastName,
        phone: operator.phone
      })

      if (result && result.data) {
        path = Paths.trips.updateStatus.replace(':id', tripId)
        await serverApi.patch<IActiveTripResponseV2>(path, {
          tripId,
          createdAt: new Date(),
          status: TripStatuses.SCHEDULED,
          data: {
            operator: {
              id: operator.operatorId,
              firstName: operator.firstName,
              lastName: operator.lastName,
              phone: operator.phone
            }
          }
        })
      }

      return result.data
      // const path = Paths.trips.updateStatus.replace(':id', tripId)
      // const result = await serverApi.patch<any>(path, {
      //   comment: 'Transferred trip to another service',
      //   tripId,
      //   createdAt: new Date(),
      //   status: TripStatuses.CANCELLED,
      //   data: { disableNotification: true }
      // })
      // return result.data.trip.id
    } else {
      const path = Paths.trips.assignOperator.replace(':id', tripId)
      const result = await serverApi.post<IActiveTripResponseV2>(path, {
        operatorId: operator.operatorId,
        firstName: operator.firstName,
        lastName: operator.lastName,
        phone: operator.phone
      })
      return result.data
    }
  }

  public async assignMultipleOperator(tripIds: string[], operator: ISimpleDriverDTO): Promise<any> {
    const result = await serverApi.post<IActiveTripResponseV2>(Paths.trips.assignMultipleOperator, {
      operatorId: operator.operatorId,
      firstName: operator.firstName,
      lastName: operator.lastName,
      tripIds,
      phone: operator.phone
    })
    return result.data
  }

  public async cancelMultipleOperator(tripIds: string[], comment: string, tripStatus: string): Promise<any> {
    const result = await serverApi.post<IActiveTripResponseV2>(Paths.trips.cancelMultipleOperator, {
      tripIds,
      comment,
      createdAt: new Date(),
      status: tripStatus
    })
    return result.data
  }
  public async manualCompleteTrip(
    tripIds: string[],
    comment: string,
    tripStatus: string,
    data: IManualCompleteTripState
  ): Promise<any> {
    const result = await serverApi.post<IActiveTripResponseV2>(Paths.trips.cancelMultipleOperator, {
      tripIds,
      comment,
      createdAt: new Date(),
      status: tripStatus,
      data
    })
    return result.data
  }

  public async completeManually(
    tripId: string,
    paymentRequired: boolean,
    price?: number,
    comment?: string,
    disableNotification?: boolean
  ): Promise<any> {
    const path = Paths.trips.updateStatus.replace(':id', tripId)
    const data: any = { paymentRequired }

    if (paymentRequired) {
      data.price = price
    }

    if (disableNotification) {
      data.disableNotification = disableNotification
    }

    const result = await serverApi.patch<any>(path, {
      comment,
      tripId,
      createdAt: new Date(),
      status: TripStatuses.MANUALLY_COMPLETED,
      data
    })
    return result.data.trip.id
  }

  public async cancelBillable(
    tripId: string,
    paymentRequired: boolean,
    price?: number,
    comment?: string,
    disableNotification?: boolean
  ): Promise<any> {
    const path = Paths.trips.updateStatus.replace(':id', tripId)
    const data: any = { paymentRequired }

    if (paymentRequired) {
      data.price = price
    }

    if (disableNotification) {
      data.disableNotification = disableNotification
    }

    const result = await serverApi.patch<any>(path, {
      comment,
      tripId,
      createdAt: new Date(),
      status: TripStatuses.CANCEL_BILLABLE,
      data
    })
    return result.data.trip.id
  }

  public async importTrips(orgId: string, awsData: { Location: string; Key: string; Bucket: string }): Promise<any> {
    const path = Paths.trips.import
    return serverApi.post<any>(path, {
      orgId,
      fileLocation: awsData.Location,
      fileKey: awsData.Key,
      s3Bucket: awsData.Bucket
    })
  }

  public async importTripsJson(dto: any): Promise<any> {
    const path = Paths.trips.importJson
    return serverApi.post<any>(path, dto)
  }

  public async updateTripPushNotification(params: IPushNotificationPayload) {
    const path = `${Paths.trips.sendPushNotification}`
    const result = await serverApi.post<any>(path, params)
    return result.data
  }

  public async saveData(tripId: string, params: ITripEdit): Promise<any> {
    const path = Paths.trips.editDetail.replace(':id', tripId)
    const result = await serverApi.patch<any>(path, params)
    return result.data
  }

  public async savePolylineBounds(tripId: string, routePolyline: string, bounds: IMapBound): Promise<any> {
    const path = Paths.trips.detail.replace(':id', tripId)
    const result = await serverApi.patch<any>(path, {
      routePolyline,
      bounds
    })
    return result.data
  }

  public async updateFeedbackStatus(tripId: string, feedbackStatus: string, prevFeedbackStatus: string): Promise<any> {
    const path = Paths.trips.editDetail.replace(':id', tripId)
    const result = await serverApi.patch<any>(path, {
      feedbackStatus,
      prevFeedbackStatus
    })
    return result.data
  }

  public async getDailyTripLog(params: IDailyTripLogReq): Promise<any> {
    const result = await serverApi.get<any>(Paths.trips.getDailyTripLog, { params })
    return result
  }

  public async getTripLogPerServiceAndDateRange(params: ITripLogReq): Promise<any> {
    const result = await serverApi.get<any>(Paths.trips.getTripLogPerServiceAndDateRange, { params })
    return result
  }

  public async getTripStatusesCount(params: ICountTripStatusFilter): Promise<any> {
    const result = await serverApi.get<any>(Paths.trips.countTripStatus, { params })
    return result
  }

  public async fetchTotalTripsPerService(orgId: string, dto: IServiceListFilterDTO): Promise<any> {
    const path = Paths.trips.totalTripsPerService.replace(':orgId', orgId)
    return serverApi.post<any[]>(path, dto)
  }
  //Weather
  public async getWeather(orgId: string, params: WeatherQueryParams): Promise<any> {
    const path = Paths.trips.weatherUpdates
    return await serverApi.get<any>(`${path}/${orgId}`, { params })
  }

  public async routeOptimization(request: IScheduledRequestedTripsFiltersDTO): Promise<any> {
    const path = Paths.routeOptimization
    return await serverApi.post<IScheduledRequestedTripsFiltersDTO>(path, request)
  }

  public async optimizeTrip(request: IUpdateOptimizedTripsDTO): Promise<any> {
    const path = Paths.optimizeTrips
    return await serverApi.post<IUpdateOptimizedTripsDTO>(path, request)
  }

  public async groupRequestedScheduledTrips(param: ITripFilters): Promise<any> {
    const result = await serverApi.post<any>(Paths.groupRides.groupRequestedScheduledTrips, param)
    return result
  }

  public async manual(request: ManualTripGroupRidesDTO): Promise<any> {
    const result = await serverApi.post(Paths.groupRides.manual, request)
    return result
  }

  public async unGroupRequestedScheduledTrips(param: ITripFilters): Promise<any> {
    const result = await serverApi.post<any>(Paths.groupRides.unGroupRequestedScheduledTrips, param)
    return result
  }

  public async getAllTemporaryVehicles(): Promise<any> {
    return await serverApi.get(Paths.temporaryVehicles)
  }

  public async getTemporaryVehicleTripsCount(serviceId: string): Promise<any> {
    return await serverApi.get(`${Paths.temporaryVehiclesTripsCount}/${serviceId}`)
  }

  public async fetchTempVehicleByServiceCount() {
    const path = Paths.trips.getTempVehicleByServiceCount
    return serverApi.get<TempVehicleCountByServiceResponseDTO>(path)
  }

  public async getScheduledRequestedTrips(params: IScheduledRequestedTripsFiltersDTO) {
    const path = Paths.trips.getScheduledRequestedTrips
    return serverApi.get<IGetScheduledRequestedTripsResponseDTO>(path, { params })
  }

  public async getDriverManifests(params: IDriverManifestsRetrieveParameters) {
    const path = Paths.trips.getDriverManifests
    return serverApi.get<IDriverManifestsAlertReport>(path, { params })
  }

  public async updatePassengerDetails(body: IPassengerDetailsPayload) {
    const path = Paths.trips.updatePassengerDetails
    return serverApi.patch(path, body)
  }
}

const tripService = new TripService()

export { tripService }



// WEBPACK FOOTER //
// ./src/services/Trip.ts